import React, { useEffect } from 'react'
import { withFormik, FormikProps, FormikValues, FormikHelpers as FormikActions, FormikBag } from 'formik'
import { object, string, ref } from 'yup'

import InputComponent from '../../../components/default-input'
import ButtonComponent from '../../../components/button'
import { getFormikFieldError } from '../../../../../utils/getErrorMessage'
import FormWrapper from '../../../components/form-wrapper'

import './index.scss'

const changePasswordValidationSchema = object().shape({
  currentPass: string()
    .required('This field is required')
    .trim(),
  newPass: string()
    .required('This field is required')
    .min(8, 'Password must be at least 8 characters')
    .trim(),
  repeatPass: string()
    .required('This field is required')
    .oneOf([ref('newPass')], 'Passwords do not match')
    .trim(),
})

const initialFormValues = {
  currentPass: '',
  newPass: '',
  repeatPass: '',
}

interface IFormProps extends FormikProps<FormikValues> {
  passwordError?: string
  onClickCancel?: VoidFunction
}

const ChangePasswordForm = (props: IFormProps) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldError,
    setFieldTouched,
    isValid,
    isSubmitting,
    passwordError = '',
    onClickCancel,
  } = props

  useEffect(() => {
    setFieldError('currentPass', passwordError)
    setFieldTouched('currentPass', true, false)
  }, [passwordError])

  return (
    <form onSubmit={handleSubmit}>
      <div className="change_pass_current">
        <InputComponent
          type="password"
          label="Current Password"
          name="currentPass"
          value={values.currentPass}
          onChange={handleChange}
          onBlur={handleBlur}
          errorMessage={getFormikFieldError('currentPass', errors, touched)}
          disabled={isSubmitting}
        />
      </div>
      <div className="change_pass_new">
        <InputComponent
          type="password"
          label="New Password"
          name="newPass"
          value={values.newPass}
          onChange={handleChange}
          onBlur={handleBlur}
          errorMessage={getFormikFieldError('newPass', errors, touched)}
          disabled={isSubmitting}
        />
      </div>
      <div className="change_pass_repeat">
        <InputComponent
          type="password"
          label="Repeat New Password"
          name="repeatPass"
          value={values.repeatPass}
          onChange={handleChange}
          onBlur={handleBlur}
          errorMessage={getFormikFieldError('repeatPass', errors, touched)}
          disabled={isSubmitting}
        />
      </div>
      <div className="change_pass_btn">
        <div className="change_pass_btn_cancel" onClick={onClickCancel}>
          CANCEL
        </div>
        <ButtonComponent
          type="submit"
          color="yellow"
          className="change_pass_btn_save"
          disabled={!isValid || isSubmitting}
        >
          SAVE CHANGES
        </ButtonComponent>
      </div>
    </form>
  )
}

interface IChangePasswordProps {
  changeUserPassword: (currentPass: string, newPass: string) => {}
  closePopup: VoidFunction
  passwordError?: string
}

const ChangePassword = ({ changeUserPassword, closePopup, passwordError = '' }: IChangePasswordProps) => {
  const submitFormHandler = async (values: FormikValues, actions: FormikActions<any>): Promise<void> => {
    await changeUserPassword(values.currentPass, values.newPass)
    actions.setSubmitting(false)
  }

  const Form: any = withFormik<IChangePasswordProps, FormikValues>({
    mapPropsToValues: () => initialFormValues,
    validationSchema: changePasswordValidationSchema,
    handleSubmit: submitFormHandler,
  })(ChangePasswordForm)

  return (
    <FormWrapper title={'Change Password'}>
      <Form onClickCancel={closePopup} passwordError={passwordError} />
    </FormWrapper>
  )
}

export default ChangePassword
